import IStoreService from '../../../storeService/IStoreService';
import {
  DataValveCDMEvent,
  GenericPublisher,
  PublishCdmEventsOptions,
  AnalyticsServiceInputType
} from '../../types';
import IAnalyticsService from '../../IAnalyticsService';
import EventNames from '../../../../config/eventNames';
import { createCDMEvent } from '../../dependencies/AnalyticsEventBuilder';
import { EventCategoryEnum } from '../../dependencies/AnalyticsEventEnums';
import { SetServiceDependencies } from '../../../../infra/startup/types';
import bindAllMethods from '../../../../utils/bindAllMethods';
import { EventServiceValueType } from '../../../eventService/types';
import { IMonitoringService } from '../../../monitoringService';
import IEventService from '../../../eventService/IEventService';
import {
  checkJWebPluginsAvailability,
  writeAnalyticsDataToValueStore,
  handlePublishNotificationError,
  subscribeForCmdEventsStratus
} from './analyticsServiceHelpers';
import { ANALYTICS_SERVICE_VERSION } from '../../analyticsServiceConstants';

/**
 * BaseAnalyticsService - Abstract base class for analytics service implementations.
 *
 * Provides common functionality shared between web and native analytics services:
 * - Plugin availability checking
 * - Event subscription management
 * - CDM event callbacks
 * - ValueStore data writing
 *
 * Subclasses must implement:
 * - init(): Platform-specific initialization logic
 * - publishCdmEvents(): Platform-specific event publishing
 */
export default abstract class BaseAnalyticsService
  implements IAnalyticsService
{
  protected _isEnabled = false;
  protected _debugEnabled = false;
  protected _version = ANALYTICS_SERVICE_VERSION;
  protected _store: IStoreService;
  protected _events: IEventService;
  protected _publisher: GenericPublisher;
  protected _monitoringService: IMonitoringService;

  constructor(analyticsProps: AnalyticsServiceInputType) {
    const { enabled } = analyticsProps || {};
    this._isEnabled = enabled;
    this._debugEnabled = analyticsProps.publishErrors;

    bindAllMethods(this);
  }

  public setDependencies({ services }: SetServiceDependencies): void {
    this._events = services.eventService;
    this._monitoringService = services.monitoringService;
    this._store = services.storeService;
  }

  public isEnabled(): boolean {
    return this._isEnabled;
  }

  public async isEnabledWithAllPlugins(): Promise<boolean> {
    const { isAvailable, shouldDisable } = await checkJWebPluginsAvailability(
      this._isEnabled
    );

    if (shouldDisable) {
      this._isEnabled = false;
    }

    return isAvailable;
  }

  protected async _writeAnalyticsDataToValueStore(
    options?: PublishCdmEventsOptions
  ): Promise<void> {
    await writeAnalyticsDataToValueStore(this._version, this._store, options);
  }

  public callbackShellSimpleUiEvent(event: EventServiceValueType): void {
    this.publishCdmEvents([
      createCDMEvent(EventCategoryEnum.simpleUi, event?.eventData)
    ]);
  }

  public callbackPublishNotificationError(event: EventServiceValueType): void {
    handlePublishNotificationError(event, this._monitoringService);
  }

  protected async _subscribeForCmdEventsStratus(): Promise<void> {
    await subscribeForCmdEventsStratus((event) =>
      this.callbackPublishNotificationError(event)
    );
  }

  protected _subscribeEvents(): void {
    // Use case #1: When should send the Simple UI event.
    this._events.subscribe(EventNames.shellAnalyticsSimpleUiEvent, (event) =>
      this.callbackShellSimpleUiEvent(event)
    );

    // Use case #2: When should send the errors from Data Collection.
    if (this._debugEnabled) {
      this._subscribeForCmdEventsStratus();
    }
  }

  // Abstract methods that must be implemented by subclasses
  public abstract init(): Promise<void>;
  public abstract publishCdmEvents(
    events: DataValveCDMEvent[],
    options?: PublishCdmEventsOptions
  ): Promise<void>;
}
